import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PERSON_GRID_HEADING_SCALE, PERSON_GRID_SECTION_ONE, PERSON_GRID_SECTION_TWO, resolveTypeScale, } from "@diality/contracts";
import * as React from "react";
import { Section } from "./_section";
import { cn } from "../lib/utils";
import { Reveal } from "../components/ui/reveal";
import { MediaSlotView } from "../render/context";
/**
 * The heading's size at each step the block offers.
 *
 * LITERAL class strings, one per step, and they have to be: Tailwind's scanner
 * only sees arbitrary values written in source, so a ramp cannot be built from a
 * number at runtime — and each block's sizes differ, so this map cannot be
 * shared with the next block that grows a size control.
 *
 * Each ramp is its desktop size scaled by the ratios `l` already used — min
 * .70, fluid .2125cqw, max .95 — so L is byte-identical to what shipped and the
 * other steps sit on the same curve. The fractions are exact rather than
 * rounded: only multiples of 80 divide cleanly by .2125, CSS is content with a
 * sub-pixel font size, and rounding would take the steps off the ratio to no
 * visible end.
 *
 * `@min-[901px]:` is a CONTAINER query, resolving against the `@container` this
 * block declares on its own section. That is what the design's breakpoint has
 * always meant here, so it is what these keep.
 */
const HEADING_SIZE = {
    xl: "text-[clamp(67.2px,20.4cqw,91.2px)] @min-[901px]:text-[96px]",
    l: "text-[clamp(56px,17cqw,76px)] @min-[901px]:text-[80px]",
    m: "text-[clamp(44.8px,13.6cqw,60.8px)] @min-[901px]:text-[64px]",
    // Not offered by this block's scale, so unreachable through the editor. Empty
    // rather than absent: the record stays total, and a step added to the scale
    // without a ramp here renders at the browser default instead of crashing.
    s: "",
    xs: "",
};
/** Desktop cards and the gap between cards retain the design's dimensions. */
const CARD_W = 284;
const GRID_GAP = 24;
/** Cap the wrapped rows at the authored column count, keeping 284px cards. */
const maxGridWidth = (columns) => columns * CARD_W + (columns - 1) * GRID_GAP;
/** The section's own gutters, stepped exactly where the design steps them. */
const SECTION_X = "px-[6.667cqw] @min-[601px]:px-6 @min-[901px]:px-12 @min-[1201px]:px-6";
/**
 * The mobile rail bleeds to the screen edges, so it cancels exactly the gutter
 * {@link SECTION_X} applied. Kept beside it so the two cannot drift apart.
 */
const RAIL_BLEED = "-mx-[6.667cqw] px-[6.667cqw] scroll-p-[6.667cqw] @min-[601px]:-mx-6 @min-[601px]:px-6 @min-[601px]:scroll-p-6";
function getInitials(name) {
    return name
        .split(/\s+/)
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}
/**
 * One profile. The photo sits above a white panel carrying the identity and a
 * bio that opens in place.
 *
 * The reveal is the same `0fr`→`1fr` grid-row collapse the Accordion uses, for
 * the same reason: it animates to the bio's natural height without measuring
 * it. Unlike the Accordion the panel is always mounted — a card's bio is a
 * paragraph, not a page section, and keeping it in the document lets the closed
 * cards agree on a resting height.
 */
export function PersonCard({ person, expanded, onExpandedChange, className, }) {
    const bioId = React.useId();
    const initials = getInitials(person.name);
    const hasBio = Boolean(person.bio);
    return (_jsxs("article", { className: cn("flex w-[284px] shrink-0 flex-col", 
        // Closed cards share the height of the tallest identity lockup, so the
        // grid's rows line up. The minimum stays in force while a bio animates
        // so an expanded card collapses smoothly back down to it.
        !expanded && "min-h-[421px]", className), children: [_jsx("div", { className: "relative aspect-[284/269] w-full overflow-hidden rounded-t-[15px] bg-[#1d1f22]", children: _jsx(MediaSlotView, { image: person.avatar, ratio: "284 / 269", label: initials, className: "size-full rounded-none object-cover", emptyRender: _jsx("div", { className: "flex size-full items-center justify-center bg-secondary text-2xl font-bold text-secondary-foreground", children: initials }) }) }), _jsxs("div", { className: cn("flex w-full flex-col items-start gap-6 overflow-hidden rounded-b-[15px] bg-card p-5", !expanded && "flex-1"), children: [_jsxs("div", { className: "flex w-full flex-col gap-2 text-card-foreground", children: [_jsxs("h3", { className: "m-0 font-sans text-xl font-light leading-[1.1] tracking-[-0.02em]", children: [person.name, person.credentials ? `, ${person.credentials}` : ""] }), person.role && (_jsx("p", { className: "m-0 text-sm font-bold uppercase tracking-[1.12px] leading-[1.4]", children: person.role }))] }), hasBio && (_jsxs("div", { className: cn("flex w-full flex-col", !expanded && "mt-auto"), children: [_jsxs("button", { type: "button", "aria-expanded": expanded, "aria-controls": bioId, onClick: () => onExpandedChange(!expanded), className: cn("inline-flex cursor-pointer items-center gap-3 whitespace-nowrap text-primary", "font-body text-sm font-bold uppercase leading-normal tracking-[1.12px]", "transition-colors hover:text-brand-blue-dark", "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"), children: [expanded ? "Close" : "Read Bio", _jsxs("span", { className: "sr-only", children: [" ", "\u2014 ", person.name, person.role ? `, ${person.role}` : ""] }), _jsxs("span", { "aria-hidden": "true", className: cn("relative block size-[15px] shrink-0", "transition-transform duration-[360ms] ease-out motion-reduce:transition-none", expanded && "rotate-45"), children: [_jsx("span", { className: "absolute left-0 top-[7px] h-px w-3 bg-current" }), _jsx("span", { className: "absolute left-0 top-[7px] h-px w-3 rotate-90 bg-current" })] })] }), _jsx("div", { id: bioId, "aria-hidden": !expanded, inert: !expanded || undefined, className: cn("grid motion-reduce:transition-none", expanded
                                    ? "visible mt-4 translate-y-0 grid-rows-[1fr] opacity-100"
                                    : "invisible mt-0 -translate-y-3 grid-rows-[0fr] opacity-0"), style: {
                                    // The 16px gap lives on the panel, not the bio, so it collapses
                                    // with it — padding would survive the `0fr` collapse and pad the
                                    // closed card. Visibility flips in at once but out only after
                                    // the collapse, so a closing bio stays on screen while it goes.
                                    transition: expanded
                                        ? "grid-template-rows 480ms var(--ease-out), margin-top 480ms var(--ease-out), opacity 320ms ease-out 70ms, transform 480ms var(--ease-out), visibility 0s linear 0s"
                                        : "grid-template-rows 360ms var(--ease-out), margin-top 360ms var(--ease-out), opacity 220ms ease-in, transform 360ms var(--ease-out), visibility 0s linear 360ms",
                                }, children: _jsx("p", { className: cn("m-0 min-h-0 text-base font-light leading-[1.55] text-card-foreground", expanded ? "overflow-visible" : "overflow-hidden"), children: person.bio }) })] }))] })] }));
}
/**
 * The grid of profile cards, and nothing else — no heading, no groups.
 *
 * A list, not a stack of divs: the people are a set of peers, so each card is
 * one addressable item for assistive tech (and for tests). `role="list"` is
 * explicit because Tailwind's preflight removes the markers, which drops list
 * semantics in Safari.
 */
export function PersonGridView({ people, columns = 3, className, }) {
    const [openIndex, setOpenIndex] = React.useState(null);
    const railRef = React.useRef(null);
    const openedAtScrollLeft = React.useRef(null);
    const closeTimer = React.useRef(null);
    const clearCloseTimer = React.useCallback(() => {
        if (closeTimer.current !== null) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);
    React.useEffect(() => clearCloseTimer, [clearCloseTimer]);
    // Safari can report scroll positions outside the rail during edge bounce.
    const scrollPosition = (rail, left = rail.scrollLeft) => Math.max(0, Math.min(left, rail.scrollWidth - rail.clientWidth));
    const changeExpanded = (index, expanded) => {
        clearCloseTimer();
        openedAtScrollLeft.current =
            expanded && railRef.current ? scrollPosition(railRef.current) : null;
        setOpenIndex(expanded ? index : null);
        const rail = railRef.current;
        const card = rail?.children[index];
        if (!expanded || !rail || !card)
            return;
        const railStyle = getComputedStyle(rail);
        if (railStyle.flexWrap !== "nowrap")
            return;
        const left = scrollPosition(rail, rail.scrollLeft +
            card.getBoundingClientRect().left -
            rail.getBoundingClientRect().left -
            (parseFloat(railStyle.scrollPaddingLeft) || 0));
        // Anchor swipe-away detection at the destination so selecting a preview
        // does not immediately dismiss its bio during the automatic scroll.
        openedAtScrollLeft.current = left;
        rail.scrollTo({
            left,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
                ? "instant"
                : "smooth",
        });
    };
    const handleScroll = (event) => {
        clearCloseTimer();
        const rail = event.currentTarget;
        const start = openedAtScrollLeft.current;
        // Test the rendered layout so a narrow CMS canvas behaves like mobile.
        if (openIndex === null ||
            start === null ||
            getComputedStyle(rail).flexWrap !== "nowrap")
            return;
        const cardWidth = rail.children[openIndex]?.getBoundingClientRect().width;
        if (!cardWidth)
            return;
        if (Math.abs(scrollPosition(rail) - start) <= (cardWidth + GRID_GAP) / 2)
            return;
        // Let native snapping settle before shrinking the rail beneath the user.
        closeTimer.current = window.setTimeout(() => {
            closeTimer.current = null;
            if (getComputedStyle(rail).flexWrap !== "nowrap" ||
                Math.abs(scrollPosition(rail) - start) <= (cardWidth + GRID_GAP) / 2)
                return;
            openedAtScrollLeft.current = null;
            setOpenIndex(null);
        }, 150);
    };
    if (people.length === 0) {
        return _jsx("p", { className: "text-center text-muted-foreground", children: "No items found." });
    }
    return (_jsx("ul", { ref: railRef, role: "list", onScroll: handleScroll, className: cn(
        // Mobile remains a swipeable rail. Wider layouts wrap fixed-width
        // cards and center each row, including an incomplete final row.
        "flex w-[100cqw] items-start snap-x snap-mandatory gap-6 overflow-x-auto overflow-y-hidden pb-2", RAIL_BLEED, "@min-[641px]:mx-0 @min-[641px]:flex-wrap @min-[641px]:w-full @min-[641px]:snap-none", "@min-[641px]:justify-center @min-[641px]:gap-x-6 @min-[641px]:gap-y-10", "@min-[641px]:overflow-visible @min-[641px]:px-0 @min-[641px]:pb-0", className), style: { maxWidth: maxGridWidth(columns) }, children: people.map((person, i) => {
            const expanded = openIndex === i;
            return (_jsx(Reveal, { delay: 0.06 + i * 0.08, 
                // Even a small preview must render to advertise the swipeable rail.
                threshold: 0, asChild: true, children: _jsx("li", { "data-team-member-detail": expanded ? "" : undefined, 
                    // Reserve the leading gutter, 24px gap and at least 32px of the
                    // next card on small phones. At 601px the gutter becomes 24px,
                    // but this cap has already reached its 284px maximum by then.
                    // Fade without translating on mobile: unrevealed offscreen
                    // cards must not add vertical overflow to the swipeable rail.
                    className: "flex w-[min(284px,calc(100cqw-6.667cqw-24px-32px))] shrink-0 snap-start @max-[641px]:translate-y-0 @min-[641px]:w-[284px]", children: _jsx(PersonCard, { className: "w-full", person: person, expanded: expanded, onExpandedChange: (next) => changeExpanded(i, next) }) }) }, `${person.name}-${i}`));
        }) }));
}
/** People belonging to a group. Untagged people fall into the first group. */
function peopleInGroup(people, group, isFirst) {
    return people.filter((person) => person.group ? person.group === group.id : isFirst);
}
/** Each section reuses the original directory title and description styles. */
export function PersonGridBlock({ data }) {
    const headingSize = resolveTypeScale(PERSON_GRID_HEADING_SCALE, data.headingSize);
    const groups = data.groups?.length
        ? data.groups
        : [{ id: "", label: data.heading ?? "", description: data.intro }];
    return (_jsx(Section, { className: "@container relative w-full overflow-x-clip", children: _jsxs("div", { className: cn("flex w-full flex-col items-center gap-6", SECTION_X), children: [data.eyebrow && (_jsx("p", { "data-person-grid-eyebrow": true, className: "type-eyebrow-lg m-0 text-center", children: data.eyebrow })), _jsx("div", { className: "flex w-full flex-col items-center gap-[144px]", children: groups.map((group, index) => (_jsxs("section", { "data-person-grid-section": index + 1, "aria-label": group.label || undefined, className: "flex w-full flex-col items-center gap-14", children: [(group.label || group.description) && (_jsxs("header", { className: "flex w-full max-w-[700px] flex-col items-center gap-12 text-center @min-[901px]:gap-4", children: [group.label && (_jsx("h2", { "data-person-grid-heading": true, "data-heading-size": headingSize, className: cn("m-0 text-balance font-display text-brand-blue font-light leading-[1.04] tracking-[-0.02em]", HEADING_SIZE[headingSize]), children: group.label })), group.description && (_jsx("p", { "data-person-grid-intro": true, className: "m-0 text-lg font-light leading-[1.55] @min-[901px]:text-xl", children: group.description }))] })), _jsx(PersonGridView, { people: data.groups?.length
                                    ? peopleInGroup(data.people, group, index === 0)
                                    : data.people, columns: data.columns ?? 3 })] }, group.id))) })] }) }));
}
/** Read current sections and preserve older saved directories. */
export function personGridFromBlock(block, resolve) {
    const s = block.scalars ?? {};
    const sectionOne = block.repeats?.sectionOnePeople;
    const sectionTwo = block.repeats?.sectionTwoPeople;
    const sections = block.repeats?.sections ?? [];
    if (sectionOne === undefined &&
        sectionTwo === undefined &&
        !sections.length) {
        return legacyPersonGridFromBlock(block, resolve);
    }
    const peopleOf = (items, group) => items.map((it) => ({
        name: it.scalars?.name || "",
        role: it.scalars?.role || undefined,
        bio: it.scalars?.bio || undefined,
        avatar: resolve(it.media?.portrait),
        group,
    }));
    const one = peopleOf(sectionOne ?? [], PERSON_GRID_SECTION_ONE);
    const two = peopleOf(sectionTwo ?? [], PERSON_GRID_SECTION_TWO);
    const sectionOneTitle = s.sectionOneTitle?.trim();
    const sectionTwoTitle = s.sectionTwoTitle?.trim();
    const groups = [];
    if (one.length || sectionOneTitle)
        groups.push({
            id: PERSON_GRID_SECTION_ONE,
            label: sectionOneTitle || s.heading || "",
            description: s.sectionOneDescription ?? s.intro,
        });
    if (two.length || sectionTwoTitle)
        groups.push({
            id: PERSON_GRID_SECTION_TWO,
            label: sectionTwoTitle || "",
            description: s.sectionTwoDescription,
        });
    groups.push(...sections.map((section, index) => ({
        id: section.id ?? `section-${index}`,
        label: section.scalars?.label ?? "",
        description: section.scalars?.description || undefined,
    })));
    const additionalPeople = (block.repeats?.people ?? []).map((item) => ({
        name: item.scalars?.name || "",
        role: item.scalars?.role || undefined,
        bio: item.scalars?.bio || undefined,
        avatar: resolve(item.media?.portrait ?? item.media?.avatar),
        group: sections.some((section) => section.id && section.id === item.scalars?.sectionId)
            ? item.scalars?.sectionId
            : (sections[0]?.id ?? groups[0]?.id),
    }));
    return {
        eyebrow: s.eyebrow?.trim() || undefined,
        heading: s.heading || undefined,
        headingSize: resolveTypeScale(PERSON_GRID_HEADING_SCALE, s.headingSize),
        intro: s.intro?.trim() || undefined,
        columns: 3,
        people: [...one, ...two, ...additionalPeople],
        groups: groups.length > 0 ? groups : undefined,
    };
}
/**
 * The shape stored before two sections existed: one `people` list tagged with a
 * hand-typed group id, and a `groups` list of tabs.
 *
 * Kept because stored blocks and code-composed content still use it, and because
 * removing it would break a story this component does not own.
 */
function legacyPersonGridFromBlock(block, resolve) {
    const s = block.scalars ?? {};
    const columns = ([2, 3, 4].includes(Number(s.columns)) ? Number(s.columns) : 3);
    const people = (block.repeats?.people ?? []).map((it) => ({
        name: it.scalars?.name || "",
        role: it.scalars?.role || undefined,
        credentials: it.scalars?.credentials || undefined,
        bio: it.scalars?.bio || undefined,
        group: it.scalars?.group || undefined,
        avatar: resolve(it.media?.avatar),
    }));
    // A group needs both an id to join on and a label to show, so a half-authored
    // row is dropped rather than rendered as a nameless tab nobody can select.
    const groups = (block.repeats?.groups ?? [])
        .map((it) => ({
        id: it.scalars?.id || "",
        label: it.scalars?.label || "",
    }))
        .filter((group) => group.id && group.label);
    return {
        eyebrow: s.eyebrow?.trim() || undefined,
        heading: s.heading || undefined,
        // A block stored before the size control existed has no such scalar, so it
        // resolves to the default — which is the size it has always rendered at.
        headingSize: resolveTypeScale(PERSON_GRID_HEADING_SCALE, s.headingSize),
        intro: s.intro?.trim() || undefined,
        columns,
        people,
        groups: groups.length > 0 ? groups : undefined,
    };
}
export const personGridBlockEntry = {
    Component: PersonGridBlock,
    fromBlock: personGridFromBlock,
};
//# sourceMappingURL=person-grid.js.map